// Libraries
import { logEvent as firebaseLogEvent, setUserId, setUserProperties } from 'firebase/analytics'

// Services
import api from '../api'
import store from '../store'

// Utilities
import firebase from './firebaseWebUtil'
import environment from './environmentUtils'
import gps from '../utility/gpsUtil'
import isEmpty from './isEmpty'
import logger from './loggerUtil'
import { AnalyticsConstants, CognitoConstants } from '../constants'

class AnalyticsUtil {
  static async init () {
    await firebase.init()
    // This is to mimic the CleverTap 'App Launched' event that fires for the native apps.
    AnalyticsUtil.logEvent(AnalyticsConstants.eventName.portalLaunched, undefined, { logMarketingEvent: true })
  }

  static setScreenName (screenName) {
    AnalyticsUtil.logEvent(AnalyticsConstants.eventName.screenView, { firebase_screen: screenName })
    store.commit('display/updateHeader', { ariaHeader: screenName })
  }

  /**
   * Set Firebase identity based on the current ProfileId from cognito.
  */
  static setUserId (id) {
    try {
      logger.debug(`Saving User Id Property: ${id}`)

      const analytics = firebase.getAnalyticInstance()
      setUserId(analytics, id, { global: true })
    } catch (e) {
      logger.error('Error setting analytics User ID')
      logger.debug(e)
    }
  }

  /**
   * Set an analytics user property.
  */
  static setUserProperty (name, value) {
    try {
      logger.debug(`Setting analytics user property: ${name}=${value}`)

      // CleverTap
      if (window.clevertap && store.state.session.canUseCleverTap) {
        window.clevertap.profile.push({ Site: { [name]: value } })
      }

      // Iterable
      const cognitoId = store.getters['session/getCognitoId']
      const email = store.getters['customer/customerEmail']
      if (!isEmpty(cognitoId) && !isEmpty(email)) {
        api.analytics.updateIterableProfile(email, { [name]: value }).catch(e => {
          logger.error(`Error setting user property ${name}, to Iterable`)
          logger.debug(e)
        })
      }

      // Firebase
      const analytics = firebase.getAnalyticInstance()
      setUserProperties(analytics, { [name.toString().toLowerCase()]: value.toString().toLowerCase() })
    } catch (e) {
      logger.error(`Error setting analytics user property ${name}`)
      logger.debug(e)
    }
  }

  static async verifyMarketingEmail (email, mdmId) {
    try {
      if (store.state.session.canUseCleverTap) {
        await api.analytics.verifyCleverTapEmail(email, mdmId)
      }
    } catch (e) {
      logger.error('Error verifying marketing email.')
      logger.debug(e)
      throw e
    }
  }

  static logEvent (name, data, options) {
    data = !isEmpty(data) ? data : {}

    data.environment = environment.environment
    data.site_brand = process.env.VUE_APP_AppBrand
    data.application_type = 'servicing'

    try {
      const analytics = firebase.getAnalyticInstance()

      if (!isEmpty(analytics)) {
        const nlsCustomerId = store.getters['customer/nlsCustomerId']
        const vergentCustomerId = store.getters['customer/tlxeCustomerId']
        const profileId = store.getters['session/getCognitoId']

        if (!isEmpty(nlsCustomerId)) {
          data[AnalyticsConstants.propName.mobileNLSCustomerId.toLowerCase()] = nlsCustomerId
        }

        if (!isEmpty(vergentCustomerId)) {
          data[AnalyticsConstants.propName.mobileTLXeCustomerId.toLowerCase()] = vergentCustomerId
        }

        if (!isEmpty(profileId)) {
          data.profile_id = profileId
        }

        logger.debug(`Logging event ${name} with options ${JSON.stringify(data)}`)

        firebaseLogEvent(analytics, name, data, { global: true })
      }
    } catch (e) {
      logger.error(`Error attempting to log event ${name}`)
      logger.debug(e)
    }

    if (!isEmpty(options) && options.logMarketingEvent === true) {
      AnalyticsUtil.logMarketingEvent(name, data)
    }
  }

  static logMarketingEvent (name, data) {
    AnalyticsUtil.recordCleverTapEvent(name, data)
    AnalyticsUtil.recordIterableEvent(name, data)
  }

  static recordIterableEvent (eventName, properties = {}) {
    try {
      const cognitoId = store.getters['session/getCognitoId']
      const email = store.getters['customer/customerEmail']

      if (!isEmpty(cognitoId) && !isEmpty(email)) {
        api.analytics.logIterableEvent(email, properties, eventName).catch(e => {
          logger.error(`Error recording event ${eventName}, to Iterable`)
          logger.debug(e)
        })
      }
    } catch (e) {
      logger.error('Error logging Iterable event')
      logger.debug(e)
    }
  }

  static recordCleverTapEvent (eventName, properties = {}) {
    try {
      if (window.hasOwnProperty('clevertap') && store.state.session.canUseCleverTap) {
        if (isEmpty(properties)) {
          logger.debug(`Logging CleverTap event: ${eventName}`)
          window.clevertap.event.push(eventName)
        } else {
          logger.debug(`Logging CleverTap event: ${eventName} with properties ${JSON.stringify(properties || '')}`)
          window.clevertap.event.push(eventName, properties)
        }
      } else {
        logger.debug('CleverTap disabled or not loaded, skipping CleverTap event')
      }
    } catch (error) {
      logger.error('Error logging CleverTap event')
      logger.debug(error)
    }
  }

  static async sendCustomerAnalytics () {
    try {
      const attributes = await api.user.getAttributes()
      const customerIdsAttribute = attributes.filter(attr => attr.getName() === CognitoConstants.lmsCustomerIdsAttribute)

      if (!isEmpty(customerIdsAttribute)) {
        const custIdsRaw = customerIdsAttribute[0].getValue()
        const custIds = !isEmpty(custIdsRaw) ? JSON.parse(custIdsRaw) : {}

        if (!isEmpty(custIds)) {
          if (!isEmpty(custIds[1])) {
            AnalyticsUtil.setUserProperty(AnalyticsConstants.propName.mobileTLXeCustomerId, custIds[1])
          }

          if (!isEmpty(custIds[2])) {
            AnalyticsUtil.setUserProperty(AnalyticsConstants.propName.mobileNLSCustomerId, custIds[2])
          }
        }
      }

      if (store.state.display.isPwa) {
        AnalyticsUtil.setUserProperty(AnalyticsConstants.eventName.pwaInstalled, true)
        AnalyticsUtil.setUserProperty(AnalyticsConstants.propName.isPwa, true)
        AnalyticsUtil.logEvent(AnalyticsConstants.eventName.pwaInstalled, { value: true }, { logMarketingEvent: true })
        AnalyticsUtil.logEvent(AnalyticsConstants.eventName.joinGroup, { value: AnalyticsConstants.itemType.progressive_web_app }, { logMarketingEvent: true })
      }

      AnalyticsUtil.setUserProperty(AnalyticsConstants.propName.isMobileGenerated, true)
    } catch (e) {
      logger.error('Unable to set customerId user properties')
      logger.debug(e)
    }
  }

  /**
   * Sets the site-wide, initialization related user properties.
   */
  static saveInitUserProperties () {
    AnalyticsUtil.setUserProperty(AnalyticsConstants.propName.environment, environment.environment)
    AnalyticsUtil.setUserProperty(AnalyticsConstants.propName.siteBrand, process.env.VUE_APP_AppBrand)
  }

  /**
   * Updates the users identity in various analytics platforms.
   */
  static async updateIdentity (newEmail = undefined) {
    logger.debug('Setting CleverTap Identity')
    try {
      const mdmPromise = store.dispatch('customer/updateMdmId').catch(e => {
        logger.error('Error getting MDM')
        logger.debug(e)
      })

      const cognitoId = store.getters['session/getCognitoId']
      const email = !isEmpty(newEmail) ? newEmail : store.getters['customer/customerEmail']

      if (window.hasOwnProperty('clevertap') && store.state.session.canUseCleverTap) {
        await mdmPromise

        const mdmId = store.state.customer.mdmId

        const profile = {
          Identity: isEmpty(mdmId) ? cognitoId : mdmId,
          CognitoId: cognitoId,
          Timezone: gps.getTimezone()
        }

        logger.debug(`CleverTap Login Info: ${JSON.stringify(profile)}`)

        window.clevertap.onUserLogin.push({ Site: profile })
      }

      if (!isEmpty(cognitoId) && !isEmpty(email)) {
        await api.analytics.createIterableProfile(email)
      }
    } catch (e) {
      logger.error('Error on MessagingUtil.updateIdentity')
      logger.debug(e)
    }
  }
}

export default AnalyticsUtil
